import { StatusCodes } from 'http-status-codes';
// import fetch from 'isomorphic-unfetch';
import { ISessionState, sessionStore } from 'packages/workspace/services/store/session-store';
import { uiStore } from 'packages/workspace/services/store/ui-store';
import type { HttpModel } from './http';
import { AuthSession, fetchAuthSession } from '@aws-amplify/auth';
import { setCookie } from 'nookies';

const initialData = {
  refreshToken: '',
  idToken: '',
  accessToken: '',
  organizationId: '',
  workspaceId: '',
  enableFeatureBGRequest: false
};

sessionStore.subscribe((data: ISessionState) => {
  if (data.discoveryParams) {
    initialData.organizationId = data.discoveryParams.organizationId;
    initialData.workspaceId = data.discoveryParams.workspaceId;
  }
  if (data.iamPrincipal) {
    initialData.enableFeatureBGRequest = data.iamPrincipal.security.enable_feature_request;
  }
});

/**
 * 
 * @returns CSRF token from cookies
 */
const getCsrfToken = () => {
  if (typeof document === 'undefined') return null; // Server-side check

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN') {
      return decodeURIComponent(value);
    }
  }
  return null;
}
/**
 * 
 * @returns returns csrf token from response header
 */
export async function fetchCsrfToken(): Promise<string | null> {
  try {
    // This assumes your backend has an endpoint that sets the CSRF cookie
    const resp = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/workspaces/api/v1/csrf-token`, {
      credentials: 'include', // Important: includes cookies in the request
    });
    const csrfToken = resp.headers.get('XSRF-TOKEN');
    setCookie(null, 'XSRF-TOKEN', csrfToken || '', {
      maxAge: 60 * 60,
      path: '/',
    });
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    return null;
  }
}

export const Http = {
  Request: async <A>(methodType: string, url: string, payload?: HttpModel.IRequestPayload, updateLoaderState = true, msgHide = false, headers = {}): Promise<A> => {
    let BaseUrl = '';
    const needsCsrf = methodType !== 'GET';

    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_USE_LOCAL) {
      if (url.startsWith('/workspace')) BaseUrl = 'http://localhost:8035';
      if (url.startsWith('/hotlines')) BaseUrl = 'http://localhost:8038';
      if (url.startsWith('/resources')) BaseUrl = 'http://localhost:8034';
      if (url.startsWith('/helprooms')) BaseUrl = 'http://localhost:8037';
    } else {
      BaseUrl = process.env.NEXT_PUBLIC_API_URL || '';
    }
    let is_private_url = false;
    let session: AuthSession | null = null;
    let uploadHeaders;
    let commonHeaders = {
      ...(initialData?.workspaceId && { 'x-rhs-workspace.id': initialData.workspaceId }),
      ...(initialData?.organizationId && { 'x-rhs-organization.id': initialData.organizationId }),
      ...(initialData?.enableFeatureBGRequest && { 'x-test': initialData?.enableFeatureBGRequest })
    }
    if (Object.entries(headers).length === 0) {
      try {
        session = await fetchAuthSession();
        is_private_url = session.tokens ? true : false;
      } catch (err) {
        console.log("fas;e case");
        is_private_url = false;
      }
      uploadHeaders = {
        ...(is_private_url && { idToken: session?.tokens?.idToken?.toString() }),
        ...(is_private_url && { accessToken: session?.tokens?.accessToken.toString() }),
        ...commonHeaders
      }
    } else {
      uploadHeaders = {
        ...headers,
        ...commonHeaders
      };
    }

    if (needsCsrf) {
      let csrfToken = getCsrfToken();

      // If no token exists, fetch a new one
      if (!csrfToken) {
        csrfToken = await fetchCsrfToken();
        if (!csrfToken) {
          throw new Error('Could not obtain CSRF token');
        }
      }
      // Add token to headers
      uploadHeaders = {
        ...uploadHeaders,
        'X-CSRF-TOKEN': csrfToken
      }
    }

    let uploadUrls = ['/hotlines/api/v1/omnichannel-oncalls.upload', '/hotlines/api/v1/eligible-people.upload']

    return new Promise((resolve, reject) => {
      if (updateLoaderState) uiStore.setLoader(true);
      fetch(`${BaseUrl}${url}`, {
        body: uploadUrls?.includes(url) ? payload : JSON.stringify(payload),
        cache: 'no-cache',
        headers: uploadUrls?.includes(url) ? uploadHeaders : {
          'content-type': 'application/json',
          ...uploadHeaders
        },
        method: `${methodType}`,
        // credentials: is_private_url ? 'include' : 'omit',
      })
        .then(async (response) => {
          if (updateLoaderState) uiStore.setLoader(false);
          if (response.status === StatusCodes.OK) {
            return response
              .json()
              .then((data) => {
                if (data.message !== '' && !msgHide) uiStore.setSuccessMessage(data.message);
                return data;
              })
              .then(resolve);
          } else if (response.status === StatusCodes.FORBIDDEN) {
            throw Error('Forbidden');
          }
          return response
            .json()
            .then((data) => {
              uiStore.setErrorMessage(data.errors?.toString());
              return data;
            })
            .then(reject);
        })
        .catch((e) => {
          if (updateLoaderState) uiStore.setLoader(false);
          reject(e);
        });
    });
  },
  DownloadRequest: async <A>(methodType: string, url: string, fileName?: string, payload?: HttpModel.IRequestPayload): Promise<A> => {
    let BaseUrl = '';
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_USE_LOCAL) {
      if (url.startsWith('/workspace')) BaseUrl = 'http://localhost:8035';
      if (url.startsWith('/hotlines')) BaseUrl = 'http://localhost:8038';
      if (url.startsWith('/resources')) BaseUrl = 'http://localhost:8034';
      if (url.startsWith('/helprooms')) BaseUrl = 'http://localhost:8037';
    } else {
      BaseUrl = process.env.NEXT_PUBLIC_API_URL || '';
    }

    let is_private_url = false;
    let session: AuthSession | null = null;
    try {
      session = await fetchAuthSession();
      is_private_url = true;
    } catch (err) {
      is_private_url = false;
    }

    return new Promise((resolve, reject) => {
      uiStore.setLoader(true);
      fetch(`${BaseUrl}${url}`, {
        body: JSON.stringify(payload),
        cache: 'no-cache',
        headers: {
          'content-type': 'application/json',
          ...(is_private_url && { idToken: session?.tokens?.idToken?.toString() }),
          ...(is_private_url && { accessToken: session?.tokens?.accessToken.toString() }),
          ...(initialData?.workspaceId && { 'x-rhs-workspace.id': initialData.workspaceId }),
          ...(initialData?.organizationId && { 'x-rhs-organization.id': initialData.organizationId }),
        },
        method: `${methodType}`,
      })
        .then(async (response) => {
          uiStore.setLoader(false);
          return response
            .blob()
            .then((blob) => {
              const url_string = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url_string;
              a.download = `${fileName}_${Date.now()}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
            })
            .then();
        })
        .catch((e) => {
          uiStore.setLoader(false);
          reject(e);
        });
    });
  },
};